// Fires a usage.heartbeat event every 60s while the tab is visible and the
// user is authenticated. Paired with the server-side api.updates.served event
// (emitted by api/src/functions/updates.ts on each load), this lets us answer
// "how long are users actually in the app" in KQL — something the server can't
// know on its own because /api/updates fires once per app open, not per
// minute of engagement.
//
// Privacy / cost notes:
//   - Event body is just { oid, tid } — no behaviour data.
//   - document.visibilityState gates the beat: backgrounded tabs don't
//     contribute, so "minutes active" measures real engagement rather than
//     idle tabs that were never closed.
//   - At 60s intervals this is ~30 events per session = negligible ingestion
//     inside App Insights' 5 GB/month free tier (see docs/analytics.md).
import type { AccountInfo } from '@azure/msal-browser';
import { getAppInsights } from './authTelemetry';

const INTERVAL_MS = 60_000;
let timer: ReturnType<typeof setInterval> | null = null;

export function startSessionHeartbeat(user: { oid: string; tid: string }): void {
  // Idempotent: calling start twice (e.g. on a React strict-mode double-invoke
  // or an account change) clears the previous timer so we never run two in
  // parallel.
  stopSessionHeartbeat();

  const beat = async () => {
    if (document.visibilityState !== 'visible') return;
    const ai = await getAppInsights();
    if (!ai) return; // init failed or no conn string (dev build); silent no-op
    try {
      ai.trackEvent({
        name: 'usage.heartbeat',
        properties: { oid: user.oid, tid: user.tid },
      });
    } catch {
      // Telemetry must never surface errors to the app.
    }
  };

  // Fire once immediately so sessions shorter than INTERVAL_MS still show up.
  void beat();
  timer = setInterval(() => void beat(), INTERVAL_MS);
}

export function stopSessionHeartbeat(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

// Derive { oid, tid } from the MSAL/Teams-synthesised account that useAuth
// resolves. localAccountId is the Entra oid in both the real MSAL case and the
// Teams-synth case (see src/auth/useAuth.ts).
export function accountToUser(acct: AccountInfo): { oid: string; tid: string } {
  return { oid: acct.localAccountId, tid: acct.tenantId };
}
